import * as loadjs from 'loadjs'
import { IBidResponse, IAuctionResults } from '../../../typings/IPrebid'
import type Blocklist from '../../blocklist/Blocklist'
import { Bidder } from '../../enums'
import { LoadJsBundle } from '../../enums/LoadJsBundle'
import EventManager from '../../eventManager/EventManager'
import { domReady, remove, removeClass, addClass } from '../../helpers/DOM'
import Logger from '../../helpers/Logger'
import type WebModel from '../../models/WebModel'
import type Refresh from '../../refresh/Refresh'
import SlotRepository from '../../slots/SlotRepository'

export const GUMGUM_PRODUCTS = [1]
export const GUMGUM_URL = '//g2.gumgum.com/javascripts/ggv2.js'
export const GUMGUM_NO_ADS_CLASS = 'ggnoads'

export default class GumGum {
  constructor(model: WebModel, blocklist: Blocklist, refresh: Refresh) {
    // GumGum does not trigger 'impressionViewable' events
    // so we create a refreshable manually when a GumGum adhesion unit wins a bid
    EventManager.on(EventManager.events.bidWon, (bid) =>
      this.handleGumGumAdhesionRefresh(bid, refresh)
    )
    EventManager.on(
      EventManager.events.bidReady,
      this.handleGumGumInScreen.bind(this)
    )

    // Need to wait for blocklist.pagesettings.blocklist to load.
    domReady(() => {
      if (
        !model.gumgum_id ||
        !model.gumgum_in_image ||
        blocklist.all ||
        blocklist.contains('in_image')
      ) {
        return this
      }

      if (model.gumgum_skip_first_image) {
        this.disableFirstImage(model.content_selector)
      }

      window.ggv2id = model.gumgum_id
      window.ggproducts = GUMGUM_PRODUCTS

      loadjs(GUMGUM_URL, LoadJsBundle.gumgum)
    })
  }

  disableFirstImage(selector: string | undefined): string | null {
    const firstImageEl = selector && document.querySelector(`${selector} img`)

    return firstImageEl
      ? (firstImageEl.className += ` ${GUMGUM_NO_ADS_CLASS}`)
      : null
  }

  isGumGumBidder = (bid: IBidResponse) =>
    bid.bidder === Bidder.gumgum || bid.s2sBidder === Bidder.gumgum

  isAdhesion = (bid: IBidResponse) => bid.adUnitCode.indexOf('adhesion') !== -1

  handleGumGumAdhesionRefresh(bid: IBidResponse, refresh: Refresh): boolean {
    if (!(this.isAdhesion(bid) && this.isGumGumBidder(bid))) {
      return false
    }

    // Don't want to treat Outstream bids like regular display gumgum adhesion bids.
    if (bid.isOutstream) {
      return false
    }

    const slot = SlotRepository.adhesionSlot
    if (!slot) {
      // should never run
      Logger.debug(`Could not find slot ${bid.adUnitCode} for refresh!`)

      return false
    }
    addClass(document.body, 'gumgumAdhesion')

    refresh.registerNewRefreshable(slot)

    return true
  }

  handleGumGumInScreen(auctionResultSet: Array<IAuctionResults>): void {
    for (const bid of auctionResultSet) {
      const slot = bid.slot
      if (!slot) {
        return
      }
      if (slot.type === 'Adhesion' && slot.isRefreshing && window.GUMGUM) {
        //  One-off to close GUMGUM ads that break out of the safeframe: https://github.com/mediavine/web-wrapper/issues/1
        if (window.GUMGUM && window.GUMGUM.isad && window.GUMGUM.removeISAd) {
          Logger.debug('Closing GumGum ad')
          window.GUMGUM.removeISAd()
        } else if (window.GUMGUM.container) {
          // Remove it from the container
          Logger.debug('Closing removing GumGum ad from container')
          remove(window.GUMGUM.container.querySelector('[id^=is_]'))
        }
        removeClass(document.body, 'adhesion')
        removeClass(document.body, 'gumgumAdhesion')

        break
      }
    }
  }
}
